"use client"

import { useState } from "react"
import { Card } from "../ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { ArrowRight, Info, X, ChevronUp } from "lucide-react"
import { Button } from "../ui/button"

export function DecisionGrid() {
  const [showCommentsDialog, setShowCommentsDialog] = useState(false)
  const [showDeskManualDialog, setShowDeskManualDialog] = useState(false)

  // Decision grid data
  const gridData = {
    doNotWrite: {
      title: "Do Not Write",
      content:
        "Unfortunately, we did not find any valid class codes from the application, so we cannot make any decision on it. A manual investigation is required.",
    },
    commentsAndGuidance: {
      title: "Comments And Guidance",
      content: [
        "General GL claims (BI, PD, slips/falls, completed ops) and key underwriting considerations (experience, background, access, project type, pollution eligibility).",
        "Eligible GL limits up to $2M occurrence/$4M aggregate, refer to Increased Limit guide.",
        "Requirement to attach Professional Liability Exclusion CG 22 79, especially if design/engineering is involved.",
        "Mold Exposure is a specific concern.",
      ],
    },
    deskManualGuidance: {
      title: "Desk Manual Guidance",
      content: [
        "If the application does not specify a Products/Completed Operations aggregate limit, set it equal to the General Aggregate limit.",
        "Do not attach the Products/Completed Operations Exclusion (CG 21 04) unless the exclusion is specifically requested on the application or the Products/Completed Operations aggregate is left blank.",
        "Always attach the Contractual Liability Exclusion (CG 21 39) for this class code.",
        "Verify that all subcontractors maintain adequate insurance coverage.",
      ],
    },
  }

  // Comments dialog data
  const commentsDialogData = [
    {
      cmi: "1531",
      comment:
        "GL claims typically result from property damage during installation and slips and falls over equipment, cords, and supplies. cmi 1531 is eligible for pollution coverage. underwriting considerations may include: --is access restricted in areas where the insured is working? --what is the experience level of the insured's employees? --does the insured run background checks on prospective employees? --does the insured take steps to protect other property in the work area?",
      relevancy: "high",
    },
    {
      cmi: "1980",
      comment:
        "this class may be eligible for primary gl limits up to $2,000,000 occurrence/$4,000,000 aggregate. see app I - increased limit guide to verify.",
      relevancy: "high",
    },
    {
      cmi: "1636",
      comment:
        "If insured is directly or indirectly (contractually or through additional insured endorsement) responsible for design, attach professional liability exclusion, cg 22 79.",
      relevancy: "high",
    },
    {
      cmi: "1220",
      comment: "Mold exposure",
      relevancy: "high",
    },
    {
      cmi: "1262",
      comment:
        'This classification applies to that portion of the operations performed by adequately insured subcontractors of the insured. operations performed by subcontractors without adequate insurance shall be classified and rated under the specific classification description for each operation. determination of the adequacy of insurance shall be made in accordance with criteria established by the company prior to policy inception. we consider "adequately insured" to mean the subcontractor has limits at least as high as those of our insured, or at least $500,000 per occurrence, $1,000,000 aggregate.',
      relevancy: "high",
    },
  ]

  // Desk manual dialog data
  const deskManualData = [
    {
      section: "Limits",
      guidance:
        "For General Liability policies, standard limits are $1M per occurrence and $2M aggregate. Higher limits may be available based on risk profile and underwriting approval.",
    },
    {
      section: "Exclusions",
      guidance:
        "Standard exclusions include CG 21 04 (Products/Completed Operations), CG 21 39 (Contractual Liability), and CG 22 79 (Professional Liability). Apply as indicated in the guidance.",
    },
    {
      section: "Subcontractors",
      guidance:
        "All subcontractors must maintain GL coverage with limits of at least $500,000 per occurrence and $1,000,000 aggregate. Certificates of insurance must be collected and verified.",
    },
    {
      section: "Special Hazards",
      guidance:
        "Mold exposure requires special attention. Consider adding the Fungi or Bacteria Exclusion (CG 21 67) for high-risk operations.",
    },
  ]

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Do Not Write Card */}
        <Card className="p-5 bg-[#FFFBE5] border-yellow-200 shadow-md">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-base font-semibold flex items-center text-orange-600">
              <Info className="h-4 w-4 mr-2 text-orange-600" />
              {gridData.doNotWrite.title}
            </h3>
          </div>
          <p className=" text-gray-700">{gridData.doNotWrite.content}</p>
        </Card>

        {/* Comments And Guidance Card */}
        <Card className="p-5 bg-[#FFF0ED] border-red-200 shadow-md">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-base font-semibold flex items-center text-orange-600">
              <Info className="h-4 w-4 mr-2 text-orange-600" />
              {gridData.commentsAndGuidance.title}
            </h3>
          </div>
          <ul className=" text-gray-700 space-y-2 list-disc pl-5">
            {gridData.commentsAndGuidance.content.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <div className="flex justify-end mt-4">
            <Button
              variant="ghost"
              size="sm"
              className="text-orange-600 hover:bg-orange-50"
              onClick={() => setShowCommentsDialog(true)}
            >
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </Card>

        {/* Desk Manual Guidance Card */}
        <Card className="p-5 bg-[#FFF8E5] border-yellow-200 shadow-md">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-base font-semibold flex items-center text-orange-600">
              <Info className="h-4 w-4 mr-2 text-orange-600" />
              {gridData.deskManualGuidance.title}
            </h3>
          </div>
          <ul className=" text-gray-700 space-y-2 list-disc pl-5">
            {gridData.deskManualGuidance.content.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <div className="flex justify-end mt-4">
            <Button
              variant="ghost"
              size="sm"
              className="text-orange-600 hover:bg-orange-50"
              onClick={() => setShowDeskManualDialog(true)}
            >
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </Card>
      </div>

      {/* Comments And Guidance Dialog */}
      <Dialog open={showCommentsDialog} onOpenChange={setShowCommentsDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-orange-600">Comments And Guidance</DialogTitle>
            <DialogClose className="absolute right-4 top-4">
              <X className="h-4 w-4" />
            </DialogClose>
          </DialogHeader>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24 text-orange-600">CMI</TableHead>
                  <TableHead className="text-orange-600">Comment</TableHead>
                  <TableHead className="w-32 text-orange-600">
                    <div className="flex items-center">
                      Relevancy
                      <ChevronUp className="ml-1 h-4 w-4" />
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commentsDialogData.map((row) => (
                  <TableRow key={row.cmi}>
                    <TableCell className="font-medium text-green-600">{row.cmi}</TableCell>
                    <TableCell>{row.comment}</TableCell>
                    <TableCell>
                      <ChevronUp className="text-green-600 h-5 w-5 mx-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="bg-gray-100 p-3 mt-4 ">
            <span className="font-medium">Line Of Business:</span> General Liability
            <span className="mx-4 font-medium">Rate Date:</span> 2025-04-01
            <span className="mx-4 font-medium">State:</span> Ohio
          </div>
        </DialogContent>
      </Dialog>

      {/* Desk Manual Guidance Dialog */}
      <Dialog open={showDeskManualDialog} onOpenChange={setShowDeskManualDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-orange-600">Desk Manual Guidance</DialogTitle>
            <DialogClose className="absolute right-4 top-4">
              <X className="h-4 w-4" />
            </DialogClose>
          </DialogHeader>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32 text-orange-600">Section</TableHead>
                  <TableHead className="text-orange-600">Guidance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deskManualData.map((row) => (
                  <TableRow key={row.section}>
                    <TableCell className="font-medium">{row.section}</TableCell>
                    <TableCell>{row.guidance}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="bg-gray-100 p-3 mt-4 ">
            <span className="font-medium">Line Of Business:</span> General Liability
            <span className="mx-4 font-medium">Manual Version:</span> 2025.1
            <span className="mx-4 font-medium">Last Updated:</span> 2025-03-15
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
